import { useState } from 'react'
import { InfoSVG, JackpotPotSVG } from '@media/svgs'
import RippleButton from '../../../../../components/ui/RippleButton'
import { REWARD_TYPE, TOURNAMENT_TYPE_LABEL } from '@constants/tournamentConstants'

// Pure render — every value pre-derived by TournamentCardController. Ports
// defenderV2's TournamentCard structure exactly: a top half (name/type/status,
// countdown-or-ended-date, schedule-or-rankings) and a bottom half (stat chips,
// footer). Used both compact (list) and expanded (`variant="detail"`, on the
// TournamentDetail screen) — same markup, CSS reflows by variant.
const TournamentCard = ({
  tournament,
  view,
  schedule,
  countdown,
  currencyCode,
  isSubmitting,
  onAccept,
  onDecline,
  onView,
  variant = 'list',
}) => {
  const [showScheduleInfo, setShowScheduleInfo] = useState(false)

  if (!tournament || !view) return null

  const rewardType = tournament.rewardType
  const prizePoolValue = rewardType === REWARD_TYPE.FREEBET ? tournament.prizePool : tournament.totalReward
  const rewardsCount = Array.isArray(tournament.slabs) ? tournament.slabs.length : 0

  return (
    <div className={`standard-normal-tournament-card-outer-container standard-normal-tournament-card-${variant}`}>
      <div className="standard-normal-tournament-card-inner-container">
        <div className="standard-normal-tournament-card-top-container">
          <div className="standard-normal-tournament-card-top-row-one">
            <div className="standard-normal-tournament-card-top-row-one-spacer" />
            <div className="standard-normal-tournament-card-title-block">
              <div className="standard-normal-tournament-card-name">{tournament.name}</div>
              <div className="standard-normal-tournament-card-type">
                {TOURNAMENT_TYPE_LABEL[tournament.type]}
              </div>
            </div>
            <div className="standard-normal-tournament-card-status-slot">
              {view.statusBadge && (
                <div
                  className={`standard-normal-tournament-card-status-badge standard-normal-tournament-card-status-${view.statusBadge.variant}`}
                >
                  {view.statusBadge.label}
                </div>
              )}
            </div>
          </div>

          <div className="standard-normal-tournament-card-top-row-two">
            {view.showCountdown && countdown && (
              <div className="standard-normal-tournament-card-countdown">
                <div className="standard-normal-tournament-card-countdown-box">
                  <div className="standard-normal-tournament-card-countdown-value">{countdown.days}</div>
                  <div className="standard-normal-tournament-card-countdown-label">Days</div>
                </div>
                <span className="standard-normal-tournament-card-countdown-separator">:</span>
                <div className="standard-normal-tournament-card-countdown-box">
                  <div className="standard-normal-tournament-card-countdown-value">{countdown.hours}</div>
                  <div className="standard-normal-tournament-card-countdown-label">Hours</div>
                </div>
                <span className="standard-normal-tournament-card-countdown-separator">:</span>
                <div className="standard-normal-tournament-card-countdown-box">
                  <div className="standard-normal-tournament-card-countdown-value">{countdown.minutes}</div>
                  <div className="standard-normal-tournament-card-countdown-label">Mins</div>
                </div>
                <span className="standard-normal-tournament-card-countdown-separator">:</span>
                <div className="standard-normal-tournament-card-countdown-box">
                  <div className="standard-normal-tournament-card-countdown-value">{countdown.seconds}</div>
                  <div className="standard-normal-tournament-card-countdown-label">Secs</div>
                </div>
              </div>
            )}

            {view.showRankings && (
              <div className="standard-normal-tournament-card-ended-container">
                <span>Ended</span>
                <span>:</span>
                <span className="standard-normal-tournament-card-ended-value">{tournament.endedDate}</span>
              </div>
            )}
          </div>

          <div className="standard-normal-tournament-card-top-row-three">
            {view.showSchedule && (
              <div className="standard-normal-tournament-card-schedule">
                <div className="standard-normal-tournament-card-schedule-text">{schedule}</div>
                <div
                  className="standard-normal-tournament-card-schedule-info-icon"
                  onMouseEnter={() => setShowScheduleInfo(true)}
                  onMouseLeave={() => setShowScheduleInfo(false)}
                  onClick={() => setShowScheduleInfo(true)}
                >
                  <InfoSVG width="12" height="12" />
                  {showScheduleInfo && (
                    <div className="standard-normal-tournament-card-schedule-tooltip">
                      Bets are considered only within this time period.
                    </div>
                  )}
                </div>
              </div>
            )}

            {view.showRankings &&
              (view.hasRank ? (
                <div className="standard-normal-tournament-card-rankings">
                  <div className="standard-normal-tournament-card-rankings-item">
                    <span>Rank</span>
                    <span>:</span>
                    <span className="standard-normal-tournament-card-rankings-value">
                      {tournament.rank}
                    </span>
                  </div>
                  <div className="standard-normal-tournament-card-rankings-item">
                    <span>Won</span>
                    <span>:</span>
                    <span className="standard-normal-tournament-card-rankings-value">
                      {tournament.won}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="standard-normal-tournament-card-not-optin-message">
                  &quot;{tournament.message}&quot;
                </div>
              ))}
          </div>
        </div>

        <div className="standard-normal-tournament-card-bottom-container">
          <div className="standard-normal-tournament-card-stats-row">
            <div className="standard-normal-tournament-card-stat-chip">
              <div className="standard-normal-tournament-card-stat-chip-title">Participants</div>
              <div className="standard-normal-tournament-card-stat-chip-value">
                {tournament.participants}
              </div>
            </div>

            <div className="standard-normal-tournament-card-stat-chip">
              <div className="standard-normal-tournament-card-stat-chip-title">Prize Pool</div>
              <div className="standard-normal-tournament-card-stat-chip-value">
                <span>{prizePoolValue}</span>
                {rewardType === REWARD_TYPE.FREEBET && <JackpotPotSVG width="12" height="12" />}
                {rewardType === REWARD_TYPE.AMOUNT && (
                  <span className="standard-normal-tournament-card-stat-chip-currency">
                    {currencyCode}
                  </span>
                )}
              </div>
            </div>

            {rewardType === REWARD_TYPE.FREEBET && (
              <div className="standard-normal-tournament-card-stat-chip">
                <div className="standard-normal-tournament-card-stat-chip-title">Prize</div>
                <div className="standard-normal-tournament-card-stat-chip-value">
                  <span>{tournament.prize}</span>
                  <span className="standard-normal-tournament-card-stat-chip-currency">
                    {currencyCode}
                  </span>
                </div>
              </div>
            )}

            {rewardType === REWARD_TYPE.AMOUNT && (
              <div className="standard-normal-tournament-card-stat-chip">
                <div className="standard-normal-tournament-card-stat-chip-title">Rewards</div>
                <div className="standard-normal-tournament-card-stat-chip-value">{rewardsCount}</div>
              </div>
            )}
          </div>

          <div className="standard-normal-tournament-card-footer-row">
            {view.footer.type === 'BUTTON' && (
              <RippleButton
                className="standard-normal-tournament-card-footer-button standard-normal-tournament-card-footer-button-full-width standard-normal-tournament-card-footer-button-view"
                onClick={onView}
              >
                {view.footer.label}
              </RippleButton>
            )}

            {view.footer.type === 'ACCEPT_DECLINE' && (
              <>
                <RippleButton
                  className="standard-normal-tournament-card-footer-button standard-normal-tournament-card-footer-button-decline"
                  onClick={onDecline}
                  disabled={isSubmitting}
                >
                  Decline
                </RippleButton>
                <RippleButton
                  className="standard-normal-tournament-card-footer-button standard-normal-tournament-card-footer-button-accept"
                  onClick={onAccept}
                  disabled={isSubmitting}
                >
                  Accept
                </RippleButton>
              </>
            )}

            {view.footer.type === 'PILL' && (
              <RippleButton
                className={`standard-normal-tournament-card-footer-button standard-normal-tournament-card-footer-button-full-width standard-normal-tournament-card-footer-pill-${view.footer.variant}`}
                disabled
              >
                {view.footer.label}
              </RippleButton>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TournamentCard
